import Cookies from 'js-cookie';

/**
 * Every path a cookie written from the current page could have been stored under: the old path-less writes defaulted to
 * the directory of the page that wrote them ("/home/taxreturns" for ".../taxreturns/settings-x"), so the same name could
 * be held at several paths at once.
 */
function candidatePaths(): string[] {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const paths = ['/'];
  let current = '';
  for (const segment of segments) {
    current += `/${segment}`;
    paths.push(current);
  }
  return paths;
}

/** Removes every copy of a cookie, whatever path an older write stored it under. */
function removeEverywhere(name: string): void {
  Cookies.remove(name, { path: '' });
  for (const path of candidatePaths()) {
    Cookies.remove(name, { path });
  }
}

/**
 * Cookie access: `get` returns '' when missing, `set` writes a session cookie for the whole site (path "/"), `delete`
 * and `deleteAll` expire every readable cookie.
 *
 * BUG-071: cookies used to be written with no path, so each page stored its own copy under its own directory and a
 * stale copy (e.g. the previous client) could shadow the current one after a refresh. Writing at "/" - and removing any
 * copy at another path first - keeps one value per name, so the selected client survives a refresh.
 */
export const cookies = {
  get(name: string): string {
    return Cookies.get(name) ?? '';
  },

  set(name: string, value: string): void {
    removeEverywhere(name);
    Cookies.set(name, value, { path: '/' });
  },

  delete(name: string): void {
    removeEverywhere(name);
  },

  deleteAll(): void {
    for (const name of Object.keys(Cookies.get())) {
      removeEverywhere(name);
    }
  },
};

/** The cookies the app itself reads and writes. */
export const CookieNames = {
  userId: 'userid',
  businessId: 'businessid',
  themeColor: 'themecolor',
  isLoggedIn: 'isLoggedin',
  clientId: 'clientId',
  clientName: 'clientName',
  companyId: 'companyid',
  returnFormId: 'returnFormId',
} as const;
